import {
  DEFAULT_STATUS_CHIP_SX,
  KYC_STATUS_CHIP_SX,
  PAYMENT_STATUS_CHIP,
  POI_STATUS_CHIP_SX,
} from "../../../theme/statusChipStyles";

export const KYC_STATUS_COLOR_MAP = {
  approved: "success",
  rejected: "error",
  pending: "warning",
};

export const KYC_STATUS_LABEL_MAP = {
  approved: "Approved",
  rejected: "Rejected",
  pending: "Pending",
};

export const POI_STATUS_LABEL_MAP = {
  accepted: "Accepted",
  approved: "Accepted",
  verified: "Accepted",
  rejected: "Rejected",
  declined: "Declined",
  input_error: "Input error",
  invalid: "Invalid",
  pending: "Pending",
  submitted: "Submitted",
  waiting_for_upload: "Waiting for upload",
};

export const buildKycRowId = (row) =>
  `${row.client_info_id}-${row.provider_account_key}`;

/** Maps backend / provider status strings to dashboard list chips (approved | rejected | pending). */
const KYC_API_STATUS_MAP = {
  verified: "approved",
  accepted: "approved",
  declined: "rejected",
  pending_poi: "pending",
  pending_review: "pending",
  submitted: "pending",
  in_review: "pending",
};

const POI_API_STATUS_MAP = {
  verified: "accepted",
  approved: "accepted",
  declined: "rejected",
};

export const normalizeKycApiStatus = (value) => {
  if (!value) return value;
  const key = String(value).toLowerCase();
  return KYC_API_STATUS_MAP[key] || key;
};

export const normalizePoiApiStatus = (value) => {
  if (!value) return value;
  const key = String(value).toLowerCase();
  return POI_API_STATUS_MAP[key] || key;
};

export const normalizeKycAttempt = (attempt) => {
  if (!attempt || typeof attempt !== "object") return null;
  return {
    id: attempt.id ?? null,
    created_at: attempt.created_at ?? attempt.createdAt ?? null,
    status: attempt.status ?? null,
    declined_reason:
      attempt.declined_reason ?? attempt.declinedReason ?? null,
  };
};

export const formatPoiStatusLabel = (value) => {
  if (!value) return "-";
  const key = String(value).toLowerCase();
  return POI_STATUS_LABEL_MAP[key] || value;
};

export const formatClientDisplayName = (row) => {
  if (!row) return "-";
  const company = String(row.company_name ?? row.companyName ?? "").trim();
  if (company) return company;

  const fullName = `${row.first_name ?? row.firstName ?? ""} ${
    row.last_name ?? row.lastName ?? ""
  }`.trim();
  if (fullName) return fullName;

  const email = String(row.email ?? "").trim();
  if (email) return email;

  const clientId = row.client_info_id ?? row.clientInfoId;
  return clientId != null ? `Client #${clientId}` : "-";
};

export const formatAttemptErrorMessage = (attempt) => {
  if (!attempt) return "-";
  const reason = attempt.declined_reason ?? attempt.declinedReason;
  if (reason) return reason;

  const status = attempt.status;
  if (!status) return "-";
  const key = String(status).toLowerCase();
  if (["input_error", "declined", "rejected", "invalid"].includes(key)) {
    return formatPoiStatusLabel(status);
  }
  return "-";
};

/**
 * Normalizes a single KYC verification row from dashboard API (camelCase / provider aliases).
 * @param {Record<string, unknown>} row
 */
export const normalizeKycApiRow = (row) => {
  if (!row || typeof row !== "object") return row;

  const clientInfoId = row.client_info_id ?? row.clientInfoId;
  const providerAccountKey =
    row.provider_account_key ?? row.providerAccountKey ?? "";
  const rawStatus = row.status ?? row.kyc_status ?? row.kycStatus;
  const rawPoi =
    row.poi_status ??
    row.provider_poi_status ??
    row.providerPoiStatus;
  const rawAttempts = row.attempts ?? row.poi_attempts ?? row.poiAttempts ?? [];
  const attempts = Array.isArray(rawAttempts)
    ? rawAttempts.map(normalizeKycAttempt).filter(Boolean)
    : [];

  const normalized = {
    client_info_id: clientInfoId,
    email: row.email ?? "",
    first_name: row.first_name ?? row.firstName ?? "",
    last_name: row.last_name ?? row.lastName ?? "",
    company_name: row.company_name ?? row.companyName ?? "",
    merchant_id: row.merchant_id ?? row.merchantId ?? "",
    provider_code: row.provider_code ?? row.providerCode ?? "",
    provider_account_key: providerAccountKey,
    status: normalizeKycApiStatus(rawStatus),
    declined_reason:
      row.declined_reason ?? row.declinedReason ?? row.decline_reason ?? null,
    retries_left: row.retries_left ?? row.retriesLeft ?? null,
    attempts,
    poi_status: normalizePoiApiStatus(rawPoi),
    updated_at:
      row.updated_at ?? row.updatedAt ?? row.last_updated ?? row.lastUpdated,
    id: buildKycRowId({
      client_info_id: clientInfoId,
      provider_account_key: providerAccountKey,
    }),
    display_name: formatClientDisplayName({
      company_name: row.company_name ?? row.companyName ?? "",
      first_name: row.first_name ?? row.firstName ?? "",
      last_name: row.last_name ?? row.lastName ?? "",
      email: row.email ?? "",
      client_info_id: clientInfoId,
    }),
  };

  if (normalized.attempts.length === 0) {
    const errorPoiStatuses = new Set([
      "declined",
      "rejected",
      "input_error",
      "invalid",
    ]);
    const poiKey = String(normalized.poi_status || "").toLowerCase();
    const hasErrorState =
      Boolean(normalized.declined_reason) || errorPoiStatuses.has(poiKey);

    if (hasErrorState) {
      normalized.attempts = [
        {
          id: null,
          created_at: normalized.updated_at ?? null,
          status: normalized.poi_status || normalized.status || null,
          declined_reason: normalized.declined_reason,
        },
      ];
    }
  }

  return normalized;
};

/**
 * Accepts paginated dashboard shapes: { data: [] } or { verifications: [] }.
 */
export const normalizeKycListResponse = (result, fallbackPage, fallbackPageSize) => {
  const rows = result?.data ?? result?.verifications ?? result?.items ?? [];
  const list = Array.isArray(rows) ? rows.map(normalizeKycApiRow) : [];

  return {
    verificationsList: list,
    page: result?.page ?? fallbackPage,
    page_size: result?.page_size ?? result?.pageSize ?? fallbackPageSize,
    total_pages: result?.total_pages ?? result?.totalPages ?? 0,
    total_records:
      result?.total_records ??
      result?.totalRecords ??
      result?.total ??
      list.length,
  };
};

/** Platform admin scope (GET /kyc-verifications without merchant_id). */
export const isKycPlatformAdmin = (userInfo) =>
  userInfo?.["acquirer-rol-data"] === "admin";

export const resolveKycMerchantId = (userInfo, acquirerData) =>
  userInfo?.["acquirer-id-merchant-data"] ||
  acquirerData?.merchantID ||
  undefined;

export const formatKycStatusChip = (value) => {
  if (!value) return null;
  const key = String(value).toLowerCase();
  const normalized = KYC_API_STATUS_MAP[key] || key;
  return {
    label: KYC_STATUS_LABEL_MAP[normalized] || value,
    sx: KYC_STATUS_CHIP_SX[normalized] || DEFAULT_STATUS_CHIP_SX,
  };
};

const POI_CHIP_STATUS_KEYS = {
  accepted: "accepted",
  approved: "accepted",
  verified: "accepted",
  rejected: "rejected",
  declined: "rejected",
  invalid: "rejected",
  input_error: "rejected",
  pending: "pending",
  submitted: "pending",
  waiting_for_upload: "pending",
};

export const formatPoiStatusChip = (value) => {
  if (!value) return null;
  const key = String(value).toLowerCase();
  const bucket = POI_CHIP_STATUS_KEYS[key] || "default";
  return {
    label: formatPoiStatusLabel(value),
    sx: POI_STATUS_CHIP_SX[bucket] || POI_STATUS_CHIP_SX.default,
  };
};

export const formatPaymentStatusChip = (value) => {
  if (!value) return null;
  const key = String(value).toUpperCase();
  const chip = PAYMENT_STATUS_CHIP[key];
  if (chip) return chip;
  return {
    label: value,
    sx: DEFAULT_STATUS_CHIP_SX,
  };
};